'use client';

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { jwtDecode } from 'jwt-decode';
import { useSession } from 'next-auth/react';
import samplesModel, { SAMPLES_MODE } from '../SamplesModel';
import samplesSelectorModel from '../SamplesSelector/SamplesSelectorModel';
import { rightLanguage } from '@/sharedTypes/helpers';
import { Sample, Album, AlbumContentItem } from '@/sharedTypes/Api';

type AlbumDetail = Album & { ownerInfo?: { username?: string; avatarUrl?: string | null } };
import {
  PiArrowLeft,
  PiCaretDown,
  PiCaretRight,
  PiPlusLight,
  PiTrashLight,
  PiDotsThreeVertical,
  PiDotsSixVerticalBold,
  PiTrashSimple,
  PiShareFatLight,
  PiHeartLight,
  PiHeartFill,
  PiXLight,
  PiCopyLight,
  PiKeyLight,
  PiSquaresFour,
  PiMusicNote,
} from 'react-icons/pi';
import { IconButton } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import favoritesStore from '@/stores/favoritesStore';
import { FavoriteType } from '@/types/favorites';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { apiSwaggerClientSide } from '@/shared/api';
import { ContentPackCode } from '@/sharedTypes/Api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';

// ---- Local content types ----
interface SampleContentItem {
  _id?: string;
  type: 'sample';
  sampleId: string;
  order: number;
}

interface SectionContentItem {
  _id?: string;
  type: 'section';
  title: string;
  order: number;
  content: SampleContentItem[];
}

type ContentItem = SectionContentItem | SampleContentItem;

// MIME type used in dataTransfer to distinguish sample drags from sortable-row drags
const SAMPLE_DRAG_TYPE = 'application/x-darpiano-sample';

// ---- Enforce sections-first ordering ----
function enforceOrder(content: ContentItem[]): ContentItem[] {
  const secs = content.filter((c) => c.type === 'section');
  const samps = content.filter((c) => c.type === 'sample');
  return [
    ...secs.map((s, i) => ({ ...s, order: i })),
    ...samps.map((s, i) => ({ ...s, order: secs.length + i })),
  ];
}

// ---- Simple array move helper ----
function move<T>(arr: T[], from: number, to: number): T[] {
  const result = [...arr];
  const [removed] = result.splice(from, 1);
  result.splice(to, 0, removed);
  return result;
}

// ---- Inline add input ----
function InlineAddInput({
  placeholder,
  onSubmit,
  onCancel,
}: {
  placeholder: string;
  onSubmit: (value: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState('');
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { ref.current?.focus(); }, []);

  return (
    <form
      className='flex items-center gap-1 px-2 py-1'
      onSubmit={(e) => {
        e.preventDefault();
        if (value.trim().length >= 1) onSubmit(value.trim());
      }}
    >
      <Input
        ref={ref}
        className='text-sm py-1 h-7'
        placeholder={placeholder}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Escape') onCancel(); }}
      />
    </form>
  );
}

// ---- Sample autocomplete ----
function SampleAutocomplete({
  onSelect,
  onCancel,
  existingSampleIds,
}: {
  onSelect: (sampleId: string) => void;
  onCancel: () => void;
  existingSampleIds: string[];
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { ref.current?.focus(); }, []);

  const results = useMemo(() => {
    if (query.length < 3) return [];
    const q = query.toLowerCase();
    return samplesSelectorModel.samples
      .filter((s) => {
        if (existingSampleIds.includes(s._id)) return false;
        const title = rightLanguage(s.metadata?.title)?.toLowerCase() || '';
        return title.includes(q);
      })
      .slice(0, 8);
  }, [query, existingSampleIds]);

  return (
    <div className='px-2 py-1'>
      <Input
        ref={ref}
        className='text-sm py-1 h-7'
        placeholder={t('albums.search_sample_placeholder')}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Escape') onCancel(); }}
      />
      {results.length > 0 && (
        <div className='border rounded-md mt-1 max-h-40 overflow-auto bg-white shadow-sm'>
          {results.map((sample) => (
            <button
              key={sample._id}
              className='w-full text-left px-2 py-1.5 text-sm hover:bg-zinc-100 truncate'
              onClick={() => onSelect(sample._id)}
            >
              {rightLanguage(sample.metadata?.title)}
            </button>
          ))}
        </div>
      )}
      {query.length >= 3 && results.length === 0 && (
        <div className='text-xs text-zinc-400 py-1'>{t('albums.no_matching_samples')}</div>
      )}
    </div>
  );
}

// ---- Drag handle icon ----
function DragHandle({
  size = 14,
  className,
}: {
  size?: number;
  className?: string;
}) {
  return (
    <div
      className={cn('cursor-grab active:cursor-grabbing text-zinc-400 hover:text-zinc-600 shrink-0', className)}
      onClick={(e) => e.stopPropagation()}
    >
      <PiDotsSixVerticalBold size={size} />
    </div>
  );
}

// ---- Draggable item wrapper (HTML5 native DnD) ----
// Ignores dragover/drop events when a SAMPLE_DRAG_TYPE is active (sample cross-section moves).
function DraggableItem({
  index,
  dragIdx,
  overIdx,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  disabled,
  children,
}: {
  index: number;
  dragIdx: number | null;
  overIdx: number | null;
  onDragStart: (idx: number) => void;
  onDragOver: (idx: number) => void;
  onDrop: (idx: number) => void;
  onDragEnd: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  if (disabled) return <>{children}</>;
  const isDragging = dragIdx === index;
  const isOver = overIdx === index && dragIdx !== null && dragIdx !== index;
  return (
    <div
      draggable
      onDragStart={(e) => { e.stopPropagation(); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(index)); onDragStart(index); }}
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes(SAMPLE_DRAG_TYPE)) return;
        e.preventDefault(); e.stopPropagation(); onDragOver(index);
      }}
      onDragLeave={(e) => { e.stopPropagation(); }}
      onDrop={(e) => {
        if (e.dataTransfer.types.includes(SAMPLE_DRAG_TYPE)) return;
        e.preventDefault(); e.stopPropagation(); onDrop(index);
      }}
      onDragEnd={(e) => { e.stopPropagation(); onDragEnd(); }}
      style={{ opacity: isDragging ? 0.4 : 1 }}
      className={cn(isOver && 'border-t-2 border-blue-400')}
    >
      {children}
    </div>
  );
}

// ---- Add menu button ----
type AddOption = { label: string; type: 'section' | 'sample' };

const ADD_TYPE_ICONS: Record<AddOption['type'], React.ReactNode> = {
  section: <PiSquaresFour size={15} />,
  sample:  <PiMusicNote size={15} />,
};

function AddMenuButton({
  options,
  onSelect,
}: {
  options: AddOption[];
  onSelect: (type: AddOption['type']) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (options.length === 1) {
    return (
      <IconButton
        data-testid={`album-add-${options[0].type}-option`}
        onClick={() => onSelect(options[0].type)}
        title={options[0].label}
      >
        <PiPlusLight size={14} />
      </IconButton>
    );
  }

  return (
    <div ref={ref} className='relative flex'>
      <IconButton data-testid='album-detail-add-btn' onClick={() => setOpen((p) => !p)}>
        <PiPlusLight size={14} />
      </IconButton>
      {open && (
        <div className='absolute right-0 top-full mt-1.5 w-48 bg-white border border-zinc-200 rounded-lg shadow-lg z-20 py-1 overflow-hidden'>
          {options.map((opt) => (
            <button
              key={opt.type}
              data-testid={`album-add-${opt.type}-option`}
              className='w-full flex items-center gap-2.5 px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-50 transition-colors'
              onClick={() => { onSelect(opt.type); setOpen(false); }}
            >
              <span className='text-zinc-400 shrink-0 text-left'>{ADD_TYPE_ICONS[opt.type]}</span>
              <span className='text-left'>{opt.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---- Sample row ----
function SampleRow({
  sampleId,
  index,
  canEdit,
  onDelete,
  onSelect,
  isSelected,
}: {
  sampleId: string;
  index: number;
  canEdit: boolean;
  onDelete: () => void;
  onSelect: () => void;
  isSelected: boolean;
}) {
  const { t } = useTranslation();
  const sample = samplesSelectorModel.samples.find((s) => s._id === sampleId);

  return (
    <div
      className={cn(
        'flex items-center gap-2 px-2 py-1 min-h-8 rounded-md cursor-pointer w-full group',
        isSelected ? 'bg-zinc-100' : 'hover:bg-zinc-100',
      )}
      onClick={onSelect}
    >
      {canEdit && <DragHandle size={12} />}
      <span className='inline-flex items-center justify-center w-5 h-5 rounded bg-zinc-200 text-[10px] font-medium text-zinc-500 shrink-0'>
        {index + 1}
      </span>
      <div className='flex-1 min-w-0'>
        <p className='text-sm truncate min-w-0' title={rightLanguage(sample?.metadata?.title)}>
          {sample ? rightLanguage(sample.metadata?.title) : (
            <span className='text-zinc-400 italic'>{t('albums.unknown_sample')}</span>
          )}
        </p>
      </div>
      {canEdit && (
        <IconButton
          className='opacity-0 group-hover:opacity-100 transition-opacity shrink-0'
          title={t('albums.remove_sample')}
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
        >
          <PiTrashLight size={14} />
        </IconButton>
      )}
    </div>
  );
}

// ---- Section row ----
function SectionRow({
  item,
  sectionId,
  index,
  canEdit,
  expanded,
  onToggle,
  onDelete,
  onUpdateContent,
  onMoveSampleHere,
}: {
  item: SectionContentItem;
  sectionId: string;
  index: number;
  canEdit: boolean;
  expanded: boolean;
  onToggle: () => void;
  onDelete: () => void;
  onUpdateContent: (content: SampleContentItem[]) => void;
  onMoveSampleHere?: (sampleId: string, fromSectionId: string, atIdx: number) => void;
}) {
  const { t } = useTranslation();
  const [addingSample, setAddingSample] = useState(false);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [overIdx, setOverIdx] = useState<number | null>(null);
  const [headerDropTarget, setHeaderDropTarget] = useState(false);

  const sortedSamples = [...(item.content || [])].sort((a, b) => a.order - b.order);

  const parseDrag = (e: React.DragEvent) => {
    const raw = e.dataTransfer.getData(SAMPLE_DRAG_TYPE);
    if (!raw) return null;
    return JSON.parse(raw) as { sampleId: string; fromSectionId: string };
  };

  const handleSampleDragStart = (e: React.DragEvent, idx: number) => {
    e.stopPropagation();
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData(SAMPLE_DRAG_TYPE, JSON.stringify({
      sampleId: sortedSamples[idx].sampleId,
      fromSectionId: sectionId,
    }));
  };

  const handleSampleDrop = (e: React.DragEvent, toIdx: number) => {
    e.preventDefault();
    e.stopPropagation();
    const data = parseDrag(e);
    if (!data) return;
    if (data.fromSectionId === sectionId) {
      if (dragIdx !== null && dragIdx !== toIdx) {
        onUpdateContent(move(sortedSamples, dragIdx, toIdx).map((s, i) => ({ ...s, order: i })));
      }
    } else {
      onMoveSampleHere?.(data.sampleId, data.fromSectionId, toIdx);
    }
    setDragIdx(null);
    setOverIdx(null);
  };

  const handleHeaderDrop = (e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes(SAMPLE_DRAG_TYPE)) return;
    e.preventDefault();
    e.stopPropagation();
    setHeaderDropTarget(false);
    const data = parseDrag(e);
    if (!data || data.fromSectionId === sectionId) return;
    onMoveSampleHere?.(data.sampleId, data.fromSectionId, sortedSamples.length);
    if (!expanded) onToggle();
  };

  const handleListDrop = (e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes(SAMPLE_DRAG_TYPE)) return;
    e.preventDefault();
    e.stopPropagation();
    const data = parseDrag(e);
    if (!data || data.fromSectionId === sectionId) return;
    onMoveSampleHere?.(data.sampleId, data.fromSectionId, sortedSamples.length);
    setDragIdx(null);
    setOverIdx(null);
  };

  const handleAddSample = (sampleId: string) => {
    const next: SampleContentItem = { type: 'sample', sampleId, order: sortedSamples.length };
    onUpdateContent([...sortedSamples, next]);
    setAddingSample(false);
  };

  return (
    <div>
      <div
        className={cn(
          'flex items-center gap-2 px-2 py-2 border-b transition-colors group',
          headerDropTarget ? 'bg-blue-50 border-blue-300' : 'bg-zinc-50 hover:bg-zinc-100',
        )}
        onDragOver={(e) => {
          if (!e.dataTransfer.types.includes(SAMPLE_DRAG_TYPE)) return;
          e.preventDefault();
          e.stopPropagation();
          setHeaderDropTarget(true);
        }}
        onDragLeave={() => setHeaderDropTarget(false)}
        onDrop={handleHeaderDrop}
      >
        {canEdit && <DragHandle size={14} />}
        <button
          data-testid={`section-title-${index}`}
          className='flex-1 flex items-center gap-1.5 text-sm font-semibold'
          onClick={onToggle}
        >
          {expanded ? <PiCaretDown size={16} /> : <PiCaretRight size={16} />}
          {item.title}
        </button>
        {canEdit && (
          <div className='flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity'>
            <IconButton
              data-testid={`album-add-sample-option`}
              title={t('albums.add_sample_direct')}
              onClick={() => { setAddingSample(true); if (!expanded) onToggle(); }}
            >
              <PiPlusLight size={14} />
            </IconButton>
            <IconButton title={t('albums.delete_section')} onClick={onDelete}>
              <PiTrashLight size={16} />
            </IconButton>
          </div>
        )}
      </div>

      {expanded && (
        <div
          className='pl-4'
          onDragOver={(e) => {
            if (!e.dataTransfer.types.includes(SAMPLE_DRAG_TYPE)) return;
            e.preventDefault();
            e.stopPropagation();
          }}
          onDrop={handleListDrop}
        >
          {canEdit && addingSample && (
            <SampleAutocomplete
              existingSampleIds={sortedSamples.map((s) => s.sampleId)}
              onSelect={handleAddSample}
              onCancel={() => setAddingSample(false)}
            />
          )}
          {sortedSamples.length === 0 && !addingSample && (
            <div className='text-xs text-zinc-400 py-2 px-2'>{t('albums.no_samples')}</div>
          )}
          {sortedSamples.map((child, idx) => {
            const sample = samplesSelectorModel.samples.find((s) => s._id === child.sampleId);
            const isSelected = sample?.identifier === samplesSelectorModel.selectedSample?.identifier;
            const isDragging = dragIdx === idx;
            const isOver = overIdx === idx && dragIdx !== idx;
            return (
              <div
                key={child._id || child.sampleId}
                draggable={canEdit}
                onDragStart={(e) => handleSampleDragStart(e, idx)}
                onDragOver={(e) => { if (!e.dataTransfer.types.includes(SAMPLE_DRAG_TYPE)) return; e.preventDefault(); e.stopPropagation(); setOverIdx(idx); }}
                onDragLeave={(e) => { e.stopPropagation(); setOverIdx(null); }}
                onDrop={(e) => { if (!e.dataTransfer.types.includes(SAMPLE_DRAG_TYPE)) return; handleSampleDrop(e, idx); }}
                onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
                style={{ opacity: isDragging ? 0.4 : 1 }}
                className={cn(isOver && 'border-t-2 border-blue-400')}
              >
                <SampleRow
                  sampleId={child.sampleId}
                  index={idx}
                  canEdit={canEdit}
                  isSelected={isSelected}
                  onDelete={() =>
                    onUpdateContent(sortedSamples.filter((_, i) => i !== idx).map((s, i) => ({ ...s, order: i })))
                  }
                  onSelect={() => {
                    if (sample) samplesSelectorModel.triggerSampleSelection(sample.identifier);
                  }}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---- Main AlbumView component ----
const AlbumView = observer(() => {
  const { t } = useTranslation();
  const [album, setAlbum] = useState<AlbumDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [addingAtRoot, setAddingAtRoot] = useState<'section' | 'sample' | null>(null);

  // Section drag state (root-level section reorder)
  const [rootDragIdx, setRootDragIdx] = useState<number | null>(null);
  const [rootOverIdx, setRootOverIdx] = useState<number | null>(null);

  // Root sample drag state
  const [rootSampleDragIdx, setRootSampleDragIdx] = useState<number | null>(null);
  const [rootSampleOverIdx, setRootSampleOverIdx] = useState<number | null>(null);

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Share dialog
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareCodes, setShareCodes] = useState<ContentPackCode[]>([]);
  const [shareLoading, setShareLoading] = useState(false);
  const [generateLoading, setGenerateLoading] = useState(false);
  const [lastGenerateTime, setLastGenerateTime] = useState(0);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);

  const { data: session } = useSession();

  let currentUserId: string | null = null;
  if (session?.user && 'sub' in session.user) {
    currentUserId = (session.user as any).sub;
  } else if (session?.accessToken) {
    try { currentUserId = (jwtDecode(session.accessToken) as any).sub; } catch { /* ignore */ }
  }

  let isAdmin = false;
  if (session?.accessToken) {
    try {
      isAdmin = (jwtDecode(session.accessToken) as any)?.realm_access?.roles?.includes('darpiano-admin') || false;
    } catch { /* ignore */ }
  }

  useEffect(() => {
    const fetchAlbum = async () => {
      if (!samplesModel.selectedAlbumId) {
        setAlbum(null);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const res = await apiSwaggerClientSide.albums.albumsDetail(samplesModel.selectedAlbumId);
        if (res.ok) {
          setAlbum(res.data as AlbumDetail);
          setExpandedItems(new Set());
        }
      } catch (error) {
        console.error('Failed to fetch album:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAlbum();
  }, [samplesModel.selectedAlbumId]);

  const canEdit = album ? (
    album.albumType === 'official' ? isAdmin :
    album.albumType === 'admin_created' ? (album.ownerId === currentUserId || isAdmin) :
    album.albumType === 'internal' ? album.ownerId === currentUserId :
    false
  ) : false;

  // ---- Save helper ----
  const saveAlbumContent = useCallback(async (updatedContent: ContentItem[]) => {
    if (!album) return;
    try {
      const res = await apiSwaggerClientSide.albums.albumsUpdate(album._id, {
        content: updatedContent as any,
      });
      if (res.ok) {
        setAlbum(res.data as AlbumDetail);
      } else {
        toast.error((res.error as any)?.error || t('albums.save_failed'));
      }
    } catch (error) {
      console.error('Failed to save album:', error);
      toast.error(t('albums.save_failed'));
    }
  }, [album, t]);

  const toggleItem = (id: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const rawContent = (album?.content as ContentItem[]) || [];
  const rootSections = [...rawContent].filter((c) => c.type === 'section').sort((a, b) => a.order - b.order) as SectionContentItem[];
  const rootSamples = [...rawContent].filter((c) => c.type === 'sample').sort((a, b) => a.order - b.order) as SampleContentItem[];

  // ---- Cross-section / cross-root sample move ----
  const handleSectionReceiveSample = useCallback((
    targetSectionIdx: number,
    sampleId: string,
    fromSectionId: string,
    atIdx: number,
  ) => {
    const newSections = rootSections.map((child, i) => {
      if (fromSectionId === 'root') return child;
      const childId = child._id || `section-${i}`;
      if (childId === fromSectionId) {
        return { ...child, content: child.content.filter((s) => s.sampleId !== sampleId) };
      }
      return child;
    });
    const newRootSamples = fromSectionId === 'root'
      ? rootSamples.filter((s) => s.sampleId !== sampleId)
      : rootSamples;
    const target = newSections[targetSectionIdx];
    const targetContent = [...target.content];
    targetContent.splice(atIdx, 0, { type: 'sample', sampleId, order: atIdx });
    newSections[targetSectionIdx] = { ...target, content: targetContent.map((s, i) => ({ ...s, order: i })) };
    saveAlbumContent(enforceOrder([...newSections, ...newRootSamples]));
  }, [rootSections, rootSamples, saveAlbumContent]);

  // ---- Root add handlers ----
  const handleRootAddSection = (title: string) => {
    setAddingAtRoot(null);
    saveAlbumContent(enforceOrder([...rootSections, { type: 'section', title, order: 0, content: [] }, ...rootSamples]));
  };

  const handleRootAddSample = (sampleId: string) => {
    setAddingAtRoot(null);
    saveAlbumContent(enforceOrder([...rootSections, ...rootSamples, { type: 'sample', sampleId, order: 0 }]));
  };

  // ---- Share dialog ----
  const GENERATE_COOLDOWN_MS = 60_000;

  const fetchShareCodes = async () => {
    if (!album) return;
    setShareLoading(true);
    try {
      const res = await apiSwaggerClientSide.albums.getAlbums();
      const codes = (res.data?.codes || [])
        .filter((c) => c.albumId === album._id)
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      setShareCodes(codes);
    } catch (err) {
      console.error('Failed to fetch share codes:', err);
    } finally {
      setShareLoading(false);
    }
  };

  const handleOpenShareDialog = () => {
    setSettingsOpen(false);
    setShareDialogOpen(true);
    fetchShareCodes();
  };

  const handleGenerateCode = async () => {
    if (!album || generateLoading) return;
    const now = Date.now();
    if (now - lastGenerateTime < GENERATE_COOLDOWN_MS) {
      setCooldownRemaining(Math.ceil((GENERATE_COOLDOWN_MS - (now - lastGenerateTime)) / 1000));
      return;
    }
    setGenerateLoading(true);
    try {
      const expiresAt = new Date(Date.now() + 60 * 60 * 1000).toISOString();
      const res = await apiSwaggerClientSide.albums.postAlbums({ albumId: album._id, expiresAt });
      if (!res.ok) { toast.error((res.error as any)?.error || t('albums.share.failed_generate')); return; }
      setLastGenerateTime(Date.now());
      setCooldownRemaining(Math.ceil(GENERATE_COOLDOWN_MS / 1000));
      await fetchShareCodes();
    } catch {
      toast.error(t('albums.share.failed_generate'));
    } finally {
      setGenerateLoading(false);
    }
  };

  const handleRevokeCode = async (code: string) => {
    try {
      const res = await apiSwaggerClientSide.albums.deleteAlbums(code);
      if (!res.ok) { toast.error((res.error as any)?.error || t('albums.share.failed_revoke')); return; }
      setShareCodes((prev) => prev.filter((c) => c.code !== code));
    } catch {
      toast.error(t('albums.share.failed_revoke'));
    }
  };

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    toast.success(t('albums.share.code_copied'));
  };

  useEffect(() => {
    if (cooldownRemaining <= 0) return;
    const timer = setInterval(() => {
      setCooldownRemaining((prev) => { if (prev <= 1) { clearInterval(timer); return 0; } return prev - 1; });
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldownRemaining]);

  const handleBack = () => {
    samplesModel.selectedAlbumId = null;
    samplesModel.currentMode = samplesModel.previousMode;
  };

  const handleDeleteAlbum = async () => {
    if (!album) return;
    setDeleteLoading(true);
    try {
      const res = await apiSwaggerClientSide.albums.albumsDelete(album._id);
      if (!res.ok) { toast.error((res.error as any)?.error || t('albums.delete.failed')); return; }
      setDeleteDialogOpen(false);
      handleBack();
    } catch {
      toast.error(t('albums.delete.failed'));
    } finally {
      setDeleteLoading(false);
    }
  };

  // ---- Loading / not found ----
  if (loading) {
    return (
      <div className='flex flex-col max-h-full overflow-hidden'>
        <div className='flex items-center gap-2 px-2 py-2 border-b'>
          <IconButton onClick={handleBack}><PiArrowLeft size={20} /></IconButton>
          <div className='h-5 w-32 bg-zinc-200 rounded animate-pulse' />
        </div>
        <div className='p-3 space-y-3'>
          {[...Array(3)].map((_, i) => (
            <div key={i} className='h-8 bg-zinc-200 rounded animate-pulse' />
          ))}
        </div>
      </div>
    );
  }

  if (!album) {
    return (
      <div className='flex flex-col max-h-full overflow-hidden'>
        <div className='flex items-center gap-2 px-2 py-2 border-b'>
          <IconButton onClick={handleBack}><PiArrowLeft size={20} /></IconButton>
          <span className='text-sm text-zinc-500'>{t('albums.album_not_found')}</span>
        </div>
      </div>
    );
  }

  const rootAddOptions: AddOption[] = [
    { label: t('albums.add_section'), type: 'section' },
    { label: t('albums.add_sample_direct'), type: 'sample' },
  ];

  return (
    <div data-testid='album-detail-view' className='flex flex-col max-h-full overflow-hidden'>
      {/* Header */}
      <div className='flex items-center gap-2 px-2 py-2 border-b justify-between'>
        <div className='flex items-center gap-2'>
          <IconButton data-testid='album-detail-back-btn' onClick={handleBack}>
            <PiArrowLeft size={20} />
          </IconButton>
          <span data-testid='album-detail-title' className='text-sm font-medium truncate'>
            {album.title}
          </span>
        </div>
        <div className='flex items-center gap-1'>
          <IconButton onClick={() => favoritesStore.toggleSingleFavorite(album._id, FavoriteType.ALBUM)}>
            {favoritesStore.isFavorite(album._id) ? <PiHeartFill size={18} /> : <PiHeartLight size={18} />}
          </IconButton>
          {canEdit && (
            <AddMenuButton options={rootAddOptions} onSelect={(type) => setAddingAtRoot(type)} />
          )}
          {(canEdit || isAdmin || album.albumType === 'internal') && (
            <IconButton
              data-testid='album-settings-button'
              title='Album settings'
              onClick={() => setSettingsOpen((prev) => !prev)}
            >
              <PiDotsThreeVertical size={18} />
            </IconButton>
          )}
        </div>
      </div>

      {settingsOpen && (
        <div className='absolute top-10 right-2 w-48 bg-white border rounded shadow-md z-10 p-3 text-sm space-y-2'>
          {(canEdit || isAdmin) && (
            <button
              data-testid='album-detail-delete-btn'
              className='w-full text-left hover:bg-zinc-100 px-2 py-1 rounded flex gap-1 items-center text-red-500'
              onClick={() => { setSettingsOpen(false); setDeleteDialogOpen(true); }}
            >
              <PiTrashSimple size={16} />
              {t('albums.delete.button')}
            </button>
          )}
          {canEdit && album.albumType === 'internal' && (
            <button
              data-testid='album-detail-share-btn'
              className='w-full text-left hover:bg-zinc-100 px-2 py-1 rounded flex gap-1 items-center'
              onClick={handleOpenShareDialog}
            >
              <PiShareFatLight size={16} />
              {t('albums.share.button')}
            </button>
          )}
          {/* TODO: remove_from_library — requires backend viewer removal endpoint */}
        </div>
      )}

      {/* Content tree */}
      <div className='overflow-auto flex-1 min-h-[30vh]'>
        {rootSections.length === 0 && rootSamples.length === 0 && !addingAtRoot && (
          <div className='text-sm text-zinc-500 text-center py-4'>
            {t('albums.no_sections')}
          </div>
        )}

        {/* Sections (always at top) */}
        {rootSections.map((item, sectionIdx) => {
          const key = item._id || `section-${sectionIdx}`;
          const sectionId = item._id || key;
          return (
            <DraggableItem
              key={key}
              index={sectionIdx}
              dragIdx={rootDragIdx}
              overIdx={rootOverIdx}
              onDragStart={setRootDragIdx}
              onDragOver={setRootOverIdx}
              onDrop={(toIdx) => {
                if (rootDragIdx === null || rootDragIdx === toIdx) return;
                saveAlbumContent(enforceOrder([...move(rootSections, rootDragIdx, toIdx), ...rootSamples]));
                setRootDragIdx(null); setRootOverIdx(null);
              }}
              onDragEnd={() => { setRootDragIdx(null); setRootOverIdx(null); }}
              disabled={!canEdit}
            >
              <SectionRow
                item={item}
                sectionId={sectionId}
                index={sectionIdx}
                canEdit={canEdit}
                expanded={expandedItems.has(sectionId)}
                onToggle={() => toggleItem(sectionId)}
                onDelete={() => saveAlbumContent(enforceOrder([...rootSections.filter((_, i) => i !== sectionIdx), ...rootSamples]))}
                onUpdateContent={(content) => {
                  const newSections = rootSections.map((s, i) => i === sectionIdx ? { ...s, content } : s);
                  saveAlbumContent(enforceOrder([...newSections, ...rootSamples]));
                }}
                onMoveSampleHere={(sampleId, fromSectionId, atIdx) =>
                  handleSectionReceiveSample(sectionIdx, sampleId, fromSectionId, atIdx)
                }
              />
            </DraggableItem>
          );
        })}

        {/* Root-level samples (always below sections) */}
        {rootSamples.map((item, sampleIdx) => {
          const sample = samplesSelectorModel.samples.find((s) => s._id === item.sampleId);
          const isSelected = sample?.identifier === samplesSelectorModel.selectedSample?.identifier;
          const isDragging = rootSampleDragIdx === sampleIdx;
          const isOver = rootSampleOverIdx === sampleIdx && rootSampleDragIdx !== null && rootSampleDragIdx !== sampleIdx;
          return (
            <div
              key={item._id || item.sampleId}
              draggable={canEdit}
              onDragStart={(e) => {
                e.stopPropagation();
                setRootSampleDragIdx(sampleIdx);
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData(SAMPLE_DRAG_TYPE, JSON.stringify({ sampleId: item.sampleId, fromSectionId: 'root' }));
              }}
              onDragOver={(e) => {
                if (!e.dataTransfer.types.includes(SAMPLE_DRAG_TYPE)) return;
                e.preventDefault(); e.stopPropagation();
                setRootSampleOverIdx(sampleIdx);
              }}
              onDragLeave={() => setRootSampleOverIdx(null)}
              onDrop={(e) => {
                if (!e.dataTransfer.types.includes(SAMPLE_DRAG_TYPE)) return;
                e.preventDefault(); e.stopPropagation();
                const raw = e.dataTransfer.getData(SAMPLE_DRAG_TYPE);
                if (!raw) return;
                const data = JSON.parse(raw) as { sampleId: string; fromSectionId: string };
                if (data.fromSectionId === 'root' && rootSampleDragIdx !== null && rootSampleDragIdx !== sampleIdx) {
                  saveAlbumContent(enforceOrder([...rootSections, ...move(rootSamples, rootSampleDragIdx, sampleIdx)]));
                }
                setRootSampleDragIdx(null); setRootSampleOverIdx(null);
              }}
              onDragEnd={() => { setRootSampleDragIdx(null); setRootSampleOverIdx(null); }}
              style={{ opacity: isDragging ? 0.4 : 1 }}
              className={cn(isOver && 'border-t-2 border-blue-400')}
            >
              <SampleRow
                sampleId={item.sampleId}
                index={sampleIdx}
                canEdit={canEdit}
                isSelected={isSelected}
                onDelete={() => saveAlbumContent(enforceOrder([...rootSections, ...rootSamples.filter((_, i) => i !== sampleIdx)]))}
                onSelect={() => { if (sample) samplesSelectorModel.triggerSampleSelection(sample.identifier); }}
              />
            </div>
          );
        })}

        {/* Root-level inline add inputs */}
        {canEdit && addingAtRoot === 'section' && (
          <InlineAddInput
            placeholder={t('albums.section_name_placeholder')}
            onSubmit={handleRootAddSection}
            onCancel={() => setAddingAtRoot(null)}
          />
        )}
        {canEdit && addingAtRoot === 'sample' && (
          <SampleAutocomplete
            existingSampleIds={rootSamples.map((c) => c.sampleId)}
            onSelect={handleRootAddSample}
            onCancel={() => setAddingAtRoot(null)}
          />
        )}
      </div>

      {/* Delete dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className='sm:max-w-md'>
          <DialogHeader>
            <DialogTitle>{t('albums.delete.title')}</DialogTitle>
            <DialogDescription>
              {t('albums.delete.confirm_prefix')}
              <span className='font-medium text-zinc-900'>{album.title}</span>
              {t('albums.delete.confirm_suffix')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button
              className='px-4 py-2 text-sm rounded-md border border-zinc-300 hover:bg-zinc-50 transition-colors'
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleteLoading}
            >
              {t('albums.delete.cancel')}
            </button>
            <button
              data-testid='album-detail-confirm-delete-btn'
              className={cn(
                'px-4 py-2 text-sm rounded-md text-white transition-colors',
                deleteLoading ? 'bg-red-400 cursor-not-allowed' : 'bg-red-500 hover:bg-red-600',
              )}
              onClick={handleDeleteAlbum}
              disabled={deleteLoading}
            >
              {deleteLoading ? t('albums.delete.deleting') : t('albums.delete.confirm')}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share dialog */}
      <Dialog open={shareDialogOpen} onOpenChange={(open) => {
        setShareDialogOpen(open);
        if (!open) setCooldownRemaining(0);
      }}>
        <DialogContent data-testid='share-dialog' className='sm:max-w-md'>
          <DialogHeader className='items-center'>
            <div className='w-14 h-14 rounded-full bg-zinc-100 flex items-center justify-center mb-2'>
              <PiKeyLight size={28} className='text-zinc-600' />
            </div>
            <DialogTitle>{t('albums.share.title')}</DialogTitle>
            <DialogDescription>{t('albums.share.description')}</DialogDescription>
          </DialogHeader>

          {shareLoading ? (
            <div className='flex justify-center py-4'>
              <div className='h-5 w-5 border-2 border-zinc-300 border-t-zinc-600 rounded-full animate-spin' />
            </div>
          ) : (
            <>
              {shareCodes.length > 0 && (
                <div className='flex gap-2'>
                  <Input
                    data-testid='share-code-input'
                    readOnly
                    value={shareCodes[0].code}
                    className='flex-1 font-mono text-center tracking-widest'
                  />
                  <button
                    data-testid='share-code-copy-btn'
                    className='shrink-0 w-10 h-10 flex items-center justify-center rounded-md border border-zinc-300 hover:bg-zinc-50 transition-colors'
                    onClick={() => handleCopyCode(shareCodes[0].code)}
                    title={t('albums.share.copy_title')}
                  >
                    <PiCopyLight size={18} />
                  </button>
                </div>
              )}
              {shareCodes.length > 0 && (
                <div className='mt-3 space-y-1 max-h-40 overflow-auto'>
                  <p className='text-xs text-zinc-500 font-medium'>{t('albums.share.codes_label')}</p>
                  {shareCodes.map((cpc) => (
                    <div key={cpc.code} className='flex items-center gap-2 text-sm py-1 px-1 rounded hover:bg-zinc-50'>
                      <span className='font-mono text-xs flex-1 truncate'>{cpc.code}</span>
                      <button
                        data-testid={`share-code-revoke-btn-${cpc.code}`}
                        className='shrink-0 flex items-center gap-1 text-xs text-zinc-400 hover:text-red-500 transition-colors'
                        onClick={() => handleRevokeCode(cpc.code)}
                        title={t('albums.share.revoke')}
                      >
                        <PiTrashLight size={14} />
                        <span>{t('albums.share.revoke')}</span>
                      </button>
                      <span className='shrink-0 text-xs text-zinc-400'>
                        {cpc.expiresAt
                          ? new Date(cpc.expiresAt).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })
                          : t('albums.share.no_expiration')}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {shareCodes.length === 0 && (
                <p className='text-sm text-zinc-500 text-center py-2'>{t('albums.share.no_codes')}</p>
              )}
            </>
          )}

          <DialogFooter>
            <button
              className='px-4 py-2 text-sm rounded-md border border-zinc-300 hover:bg-zinc-50 transition-colors'
              onClick={() => setShareDialogOpen(false)}
            >
              {t('albums.share.cancel')}
            </button>
            <button
              data-testid='share-code-generate-btn'
              className={cn(
                'px-4 py-2 text-sm rounded-md text-white transition-colors',
                generateLoading || cooldownRemaining > 0
                  ? 'bg-accent-blue/50 cursor-not-allowed'
                  : 'bg-accent-blue hover:bg-accent-blue/90',
              )}
              onClick={handleGenerateCode}
              disabled={generateLoading || cooldownRemaining > 0}
            >
              {generateLoading
                ? t('albums.share.generating')
                : cooldownRemaining > 0
                  ? t('albums.share.wait_cooldown', { seconds: cooldownRemaining })
                  : t('albums.share.generate')}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
});

export default AlbumView;
